import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import ActionCard, { ActionCardVariant } from 'components/Common/Cards/ActionCard';
// import { LoanActionCard } from 'components/FinancialCheckup/FinancialDashboard/YourPlanneryLoanSection';
import FinancialDashboardNavigation from 'components/FinancialCheckup/FinancialDashboardNavigation';
import UpcomingSection from 'components/FinancialCheckup/FinancialDashboard/UpcomingSection';
import FinancialDashboardFormContainer from 'components/Common/FinancialDashboardFormContainer';
import LoanProgressCard from 'components/FinancialCheckup/FinancialDashboard/Loan/LoanProgressCard';
import Skeleton from 'components/Common/Skeleton';

// import { ReactComponent as AssignmentIcon } from 'images/assignment-icon.svg';
// import { ReactComponent as EditIcon } from 'images/edit-icon.svg';

import { FlowComponentType } from 'routes/types';
import { getLoanBreakdown as getLoanBreakdownThunk } from 'thunks';
import { getApplicationData } from 'selectors/getApplicationData';
import { getLoanBreakdown } from 'selectors/getLoanBreakdown';
import { formatMonetaryAmount } from 'utils/formatMonetaryAmount';
import { formatShortDate } from 'utils/dateUtils';
import { LoanBreakdownItem } from 'api/ApplicationDataApi';
import { PayFrequencyOptionLabel, PayFrequencyValueToLabel } from 'handlers/yourTotalIncome';

import styles from './ManageYourLoan.module.scss';

// TODO: DEPRIORITIZED: PL-2392 and PL-2387
// TODO: Convert to handle next actions
// export enum CompactActionCard {
//   LoanStatements = 'LoanStatements',
//   PaymentSettings = 'PaymentSettings',
// }

// const compactActionCards: LoanActionCard<CompactActionCard>[] = [
//   {
//     icon: <AssignmentIcon />,
//     label: 'Loan statements',
//     action: CompactActionCard.LoanStatements,
//   },
//   {
//     icon: <EditIcon />,
//     label: 'Payment settings',
//     action: CompactActionCard.PaymentSettings,
//   },
// ];

interface LoanDetail {
  label: string;
  value: string;
}

const DEFAULT_LOAN_DETAILS_ITEMS_LENGTH = 5;
const getLoanDetailsArray = (breakdown: LoanBreakdownItem, payFrequency: PayFrequencyOptionLabel): LoanDetail[] => [
  {
    label: 'Next due amount',
    value: formatMonetaryAmount(breakdown.nextPaymentAmount, true),
  },
  ...(breakdown.nextPaymentDate
    ? [
        {
          label: 'Next due date',
          value: formatShortDate(new Date(breakdown.nextPaymentDate)),
        },
      ]
    : []),
  {
    label: 'Billing schedule',
    value: payFrequency,
  },
  {
    label: 'Loan payoff date',
    value: formatShortDate(new Date(breakdown.finalPaymentDate)),
  },
  {
    label: 'Loan payoff amount',
    value: formatMonetaryAmount(breakdown.payoffAmount, true),
  },
];

const getPaidOffLoanDetailsArray = (breakdown: LoanBreakdownItem): LoanDetail[] => [
  {
    label: 'Loan payoff date',
    value: formatShortDate(new Date(breakdown.finalPaymentDate)),
  },
  {
    label: 'Loan payoff amount',
    value: formatMonetaryAmount(breakdown.totalLoanAmount, true),
  },
];

const ManageYourLoan = ({ navigationInfo }: FlowComponentType) => {
  const dispatch = useDispatch();
  const { loanId } = useParams<{ loanId: string }>();
  const { application } = useSelector(getApplicationData);
  const { breakdowns, isLoading } = useSelector(getLoanBreakdown);

  useEffect(() => {
    if (application?.id && breakdowns.length === 0 && !isLoading) {
      dispatch(getLoanBreakdownThunk(application.id));
    }
  }, [application]);

  const breakdown = loanId ? breakdowns.find((loan) => loan.id.toString() === loanId) : breakdowns[0];

  const isPaidOff = !isLoading && breakdown != null && !breakdown.nextPaymentDate;

  let loanDetails: LoanDetail[];
  if (breakdown && !isLoading) {
    loanDetails = isPaidOff
      ? getPaidOffLoanDetailsArray(breakdown)
      : getLoanDetailsArray(breakdown, PayFrequencyValueToLabel[breakdown.paymentFrequency]);
  } else {
    loanDetails = Array.from({ length: DEFAULT_LOAN_DETAILS_ITEMS_LENGTH });
  }

  return (
    <>
      <FinancialDashboardNavigation {...navigationInfo} />
      <FinancialDashboardFormContainer
        title={
          isLoading || !breakdown ? (
            <Skeleton width="180px" height="48px" />
          ) : (
            formatMonetaryAmount(isPaidOff ? breakdown.totalLoanAmount : breakdown.principalBalance, true)
          )
        }
        subtitle={isPaidOff ? 'Paid off' : 'Remaining balance'}
      >
        {isPaidOff ? (
          <div className={styles.section}>
            <ActionCard variant={ActionCardVariant.White}>
              <ActionCard.Col className={styles.paidOffCard}>
                <div className={styles.paidOffBadgeRow}>
                  <span>🎉</span>
                  <span className={styles.paidOffBadge}>You did it!</span>
                </div>
                <p className={styles.sectionTitle}>Your loan is paid off</p>
                <p className={styles.paidOffDescription}>
                  Last payment on {formatShortDate(new Date(breakdown!.finalPaymentDate))}
                </p>
              </ActionCard.Col>
            </ActionCard>
          </div>
        ) : (
          <LoanProgressCard
            isLoading={isLoading || !breakdown}
            periodsRemaining={breakdown?.periodsRemaining || 0}
            principalBalance={breakdown?.principalBalance || 0}
            totalLoanAmount={breakdown?.totalLoanAmount || 0}
            paymentsMade={breakdown ? breakdown.totalPayments - breakdown.periodsRemaining : 0}
            totalPayments={breakdown?.totalPayments || 0}
          />
        )}

        {/* // TODO: Backend is not ready yet for this feature, PL-2392 and PL-2387 */}
        {/* <div className={styles.section}>
          <p className={styles.sectionTitle}>Payments</p>

          <ActionCard variant={ActionCardVariant.White} className={styles.cardContainer}>
            <ActionCard.Row>
              <ActionCard.Col className={styles.paymentCardContainer}>
                <ActionCard.Title>
                  Make an <br />
                  extra payment
                </ActionCard.Title>
                <ActionCard.Description>
                  Next payment is <br /> scheduled for Sept 6.
                </ActionCard.Description>
              </ActionCard.Col>
              <ActionCard.Col className={styles.buttonContainer}>
                <ActionCard.Button className={styles.cardButton}>Pay</ActionCard.Button>
              </ActionCard.Col>
            </ActionCard.Row>
          </ActionCard>

          <div className={styles.horizontalCardsContainer}>
            {compactActionCards.map((card) => (
              <ActionCard key={card.label} variant={ActionCardVariant.White} size={ActionCardSize.Compact} showChevron>
                <ActionCard.Icon>{card.icon}</ActionCard.Icon>
                <ActionCard.Description>{card.label}</ActionCard.Description>
              </ActionCard>
            ))}
          </div>
        </div> */}

        <div className={styles.section}>
          <p className={styles.sectionTitle}>Loan details</p>

          <ActionCard variant={ActionCardVariant.White}>
            <ActionCard.Col className={styles.loanDetailsContainer}>
              {loanDetails.map((detail, index) => (
                <ActionCard.Row key={index}>
                  {isLoading || !breakdown ? (
                    <>
                      <Skeleton width="75%" height="16px" mt="12px" />
                      <Skeleton width="25%" height="16px" mt="12px" />
                    </>
                  ) : (
                    <>
                      <p className={styles.loanDetailsLabel}>{detail.label}</p>
                      <p className={styles.loanDetailsValue}>{detail.value}</p>
                    </>
                  )}
                </ActionCard.Row>
              ))}
            </ActionCard.Col>
          </ActionCard>
        </div>

        <div className={styles.section}>
          <UpcomingSection applicationId={breakdown?.applicationId} />
        </div>
      </FinancialDashboardFormContainer>
    </>
  );
};

export default ManageYourLoan;
